import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type JobProgressionViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionViewModel'
import { type OverrideableJobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import type { AlertErrorHandlerFactory } from '@/features/shell'

interface StepOption {
  id: OverrideableJobProgressionStepStatus
  display: string
}

/**
 * Lets an admin move a job to another progression step.
 */
export class UpdateProgressionStepDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable.ref
  accessor selectedStepOption: StepOption | undefined = undefined

  constructor(
    private readonly _jobProgressionVm: JobProgressionViewModel,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The step the job is on.
   */
  @computed
  get currentStepTitle() {
    return this._jobProgressionVm.currentStepInfo?.title
  }

  /**
   * The steps the job can be moved to.
   */
  @computed
  get steps(): StepOption[] {
    return this._jobProgressionVm.overrideableProgressionSteps.map(
      ([stepId, stepValue]) => ({
        id: stepId,
        display: stepValue.title,
      }),
    )
  }

  /**
   * Shows the dialog with the first step selected.
   */
  @action.bound
  show() {
    this.selectProgressionStep(this.steps[0])
    this.modal.show()
  }

  /**
   * Selects the step to move the job to.
   */
  @action.bound
  selectProgressionStep(option?: StepOption) {
    this.selectedStepOption = option
  }

  /**
   * Moves the job to the selected step.
   */
  update = task.resolved(async () => {
    if (!this.selectedStepOption) {
      return
    }

    await this._jobProgressionVm
      .overrideProgressionStep(this.selectedStepOption.id)
      .catch(
        this._createAlertErrorHandler('Updating the progression step failed'),
      )
    this.modal.close()
  })
}
